import {
    AuthenticationMethodType,
    UserStatusType,
    VerificationMethodType,
} from '@treasury/domain/company/user';
import { CompanyUserChallengeDetails } from '@treasury/domain/company/user/entities';
import { RadioOption } from '@treasury/presentation/components/jha-wc/tm-radio-button';

export type SuspensionHourOption = { label: string; value: number };

const suspensionHourOptions: SuspensionHourOption[] = [
    { label: '15 minutes', value: 1 },
    { label: '1 hour', value: 2 },
    { label: '3 hours', value: 3 },
    { label: '6 hours', value: 4 },
    { label: '12 hours', value: 5 },
    { label: '24 hours', value: 6 },
];

const verificationMethodOptions: RadioOption[] = [
    { value: VerificationMethodType.Text, label: 'Text Message' },
    { value: VerificationMethodType.Phone, label: 'Automated Phone Call' },
];

export class CompanyUserChallengeDetailsViewModel extends CompanyUserChallengeDetails {
    constructor(entity: CompanyUserChallengeDetails) {
        super(entity.toDto());
    }

    public get authMethodText() {
        switch (this.authMethod) {
            case AuthenticationMethodType.Disabled:
                return 'Disabled';
            case AuthenticationMethodType.Oob:
                return 'Out-of-Band';
            case AuthenticationMethodType.SecureToken:
                return 'Secure Token';
            default:
                return '';
        }
    }

    public get userStatusText() {
        switch (this.userStatus) {
            case UserStatusType.Unregistered:
                return 'Unregistered';
            case UserStatusType.Registered:
                return 'Registered';
            case UserStatusType.Suspended:
                return 'Suspended';
            case UserStatusType.Locked:
                return 'Locked';
            default:
                return '';
        }
    }

    public get textNumber() {
        return this.formatPhoneNumber(this.dto.oobMessagePhoneNumber);
    }

    public get phoneNumber() {
        return this.formatPhoneNumber(this.dto.oobMessagePhoneNumber);
    }

    public get abbreviatedAuthenticationMethod() {
        switch (this.authMethod) {
            case AuthenticationMethodType.Disabled:
                return 'Disabled';
            case AuthenticationMethodType.Oob:
                return 'OOB';
            case AuthenticationMethodType.SecureToken:
                return 'Token';
            default:
                return '';
        }
    }

    public get authenticationSuspendedText() {
        if (!this.dto.localSuspendEndTime) {
            return '';
        }
        const suspendEndTime = new Date(this.dto.localSuspendEndTime);
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        };

        const formattedDate = new Intl.DateTimeFormat('en-US', options).format(suspendEndTime);
        return `Authentication is suspended for this user until ${formattedDate}.`;
    }

    private formatPhoneNumber(value?: string) {
        if (value) {
            return `${value.substr(0, 3)}-${value.substr(3, 3)}-${value.substr(6, 4)}`;
        }
        return '';
    }

    public get isAuthSuspended() {
        const savedEndTime = this.dto.utcSuspendEndTime;
        if (!savedEndTime) {
            return false;
        }

        const suspendTime = savedEndTime.endsWith('Z') ? `${savedEndTime}` : `${savedEndTime}Z`;
        return new Date(suspendTime).getTime() > Date.now();
    }

    public get isAuthMethodDisabled() {
        return this.authMethod === AuthenticationMethodType.Disabled;
    }

    public get isAuthMethodOob() {
        return this?.authMethod === AuthenticationMethodType.Oob;
    }

    public get isAuthMethodSecureToken() {
        return this.authMethod === AuthenticationMethodType.SecureToken;
    }

    public get isUserRegistered() {
        return this.userStatus !== UserStatusType.Unregistered;
    }

    public get isUserLocked() {
        return this.userStatus === UserStatusType.Locked;
    }

    public get suspensionHourOptions() {
        return suspensionHourOptions;
    }

    public get verificationMethodOptions() {
        return verificationMethodOptions;
    }
}
